import { useEffect, useState } from "react";
import { Page, Layout, Card, Text, BlockStack, InlineGrid, IndexTable, Spinner } from "@shopify/polaris";
import { api } from "../api";
import type { DashboardStats, PrintJob } from "../types";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";

interface DashboardResponse {
  stats: DashboardStats;
  recent_jobs: PrintJob[];
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function Dashboard(): JSX.Element {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<DashboardResponse>("/dashboard")
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Page title="Dashboard">
        <Spinner accessibilityLabel="Loading dashboard" size="large" />
      </Page>
    );
  }

  const stats = data?.stats ?? { labelsPurchased: 0, totalCostCents: 0, jobsPrinted: 0 };
  const recentJobs = data?.recent_jobs ?? [];

  return (
    <Page title="Dashboard" subtitle="Today's shipping activity">
      <Layout>
        <Layout.Section>
          <InlineGrid columns={3} gap="400">
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm" tone="subdued">
                  Labels purchased today
                </Text>
                <Text as="p" variant="heading2xl">
                  {stats.labelsPurchased}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm" tone="subdued">
                  Spent on labels today
                </Text>
                <Text as="p" variant="heading2xl">
                  {formatCurrency(stats.totalCostCents)}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm" tone="subdued">
                  Jobs printed today
                </Text>
                <Text as="p" variant="heading2xl">
                  {stats.jobsPrinted}
                </Text>
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>

        <Layout.Section>
          {recentJobs.length === 0 ? (
            <EmptyState heading="No print jobs yet">
              <p>Once a paid order is labelled, it shows up here. No rule required.</p>
            </EmptyState>
          ) : (
            <Card padding="0">
              <IndexTable
                resourceName={{ singular: "print job", plural: "print jobs" }}
                itemCount={recentJobs.length}
                headings={[
                  { title: "Order" },
                  { title: "Status" },
                  { title: "Carrier" },
                  { title: "Tracking" },
                  { title: "Cost" },
                ]}
                selectable={false}
              >
                {recentJobs.map((job, index) => (
                  <IndexTable.Row id={String(job.id)} key={job.id} position={index}>
                    <IndexTable.Cell>{job.shopify_order_name ?? job.shopify_order_id}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <StatusBadge status={job.status} />
                    </IndexTable.Cell>
                    <IndexTable.Cell>{job.carrier ?? "—"}</IndexTable.Cell>
                    <IndexTable.Cell>{job.tracking_number ?? "—"}</IndexTable.Cell>
                    <IndexTable.Cell>{job.cost_cents ? formatCurrency(job.cost_cents) : "—"}</IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </Card>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}
